import {DEBUG} from '@/shared/config';
import {logEvent as axonLogEvent} from '@/shared/lib/axon';
import {centsToCurrency} from '@/shared/lib/currency/centsToCurrency';
import {logEvent as firebaseLogEvent} from '@/shared/lib/firebase';
import {
  logEvent as tiktokLogEvent,
  trackPageView as tiktokTrackPageView,
} from '@/shared/lib/tiktok';

import {facebookService} from './facebook';

export function analyticsEvent(
  name: string,
  params?: {[name: string]: string}
): void {
  if (DEBUG) {
    console.log('Analytics event:', name);
  }

  firebaseLogEvent(name, params);
  facebookService.track({
    method: 'trackCustom',
    event: name,
    params,
  });
}

export function analyticsScreen(componentName: string): void {
  firebaseLogEvent(`screen_${componentName}`, {
    screen_name: componentName,
    screen_class: componentName,
  });
  axonLogEvent('page_view');
  tiktokTrackPageView();
}

export function analyticsSignIn(method: string): void {
  firebaseLogEvent('sign_in', {method});
  facebookService.track({
    method: 'trackCustom',
    event: 'sign_in',
    params: {
      method,
    },
  });
  axonLogEvent('login');
}

export function analyticsSignUp(method: string): void {
  firebaseLogEvent('sign_up', {method});
  facebookService.track({
    method: 'trackCustom',
    event: 'sign_up',
    params: {method, currency: 'USD', value: 0},
  });
  axonLogEvent('sign_up', {method});
  tiktokLogEvent('CompleteRegistration');
}

type AnalyticsPurchaseParams = {
  totalPrice: number;
  subtotalPrice: number;
  tax: number;
  id: number;
  sku: string;
  currency: string;
  transactionId: string;
};

type AnalyticsAxonEcommerceItem = {
  original_price: number;
  id: number;
  sku: string;
  currency: string;
};

type AxonEcommerceItem = {
  item_id: string;
  item_variant_id: string;
  item_name: string;
  price: number;
  quantity: number;
};

type AxonEcommerceEventData = {
  currency: string;
  value: number;
  items: AxonEcommerceItem[];
};

type AxonPurchaseEventData = AxonEcommerceEventData & {
  shipping: number;
  tax: number;
  transaction_id: string;
};

function createAxonEcommerceEventData({
  original_price,
  id,
  sku,
  currency,
}: AnalyticsAxonEcommerceItem): AxonEcommerceEventData {
  const price = centsToCurrency(original_price);

  return {
    currency,
    value: price,
    items: [
      {
        item_id: String(id),
        item_variant_id: sku,
        item_name: sku,
        price,
        quantity: 1,
      },
    ],
  };
}

function createTikTokEcommerceEventData({
  original_price,
  id,
  sku,
  currency,
}: AnalyticsAxonEcommerceItem): TikTokPixelEventProperties {
  const price = centsToCurrency(original_price);

  return {
    content_type: 'product',
    content_ids: [String(id)],
    content_name: sku,
    currency,
    quantity: 1,
    value: price,
  };
}

export function analyticsAddToCart(item: AnalyticsAxonEcommerceItem): void {
  axonLogEvent('add_to_cart', createAxonEcommerceEventData(item));
  tiktokLogEvent('AddToCart', createTikTokEcommerceEventData(item));
}

export function analyticsBeginCheckout(item: AnalyticsAxonEcommerceItem): void {
  axonLogEvent('begin_checkout', createAxonEcommerceEventData(item));
  tiktokLogEvent('InitiateCheckout', createTikTokEcommerceEventData(item));
}

export function analyticsPurchase({
  totalPrice,
  subtotalPrice,
  tax,
  id,
  sku,
  currency,
  transactionId,
}: AnalyticsPurchaseParams): void {
  /** @deprecated move to clear purchase event below */
  firebaseLogEvent(`purchase_${sku}`, {
    value: totalPrice,
    items: [
      {
        sku,
        item_id: id,
      },
    ],
  });
  firebaseLogEvent('purchase', {
    currency,
    value: totalPrice,
    items: [
      {
        item_id: id,
        sku,
      },
    ],
  });

  if (transactionId.length > 0) {
    const axonPurchaseEventData: AxonPurchaseEventData = {
      currency,
      value: subtotalPrice,
      shipping: 0,
      tax,
      transaction_id: transactionId,
      items: [
        {
          item_id: String(id),
          item_variant_id: sku,
          item_name: sku,
          price: subtotalPrice,
          quantity: 1,
        },
      ],
    };

    axonLogEvent('purchase', axonPurchaseEventData, {
      dedupe_id: transactionId,
    });
  }

  facebookService.track({
    method: 'track',
    event: 'Purchase',
    params: {
      currency,
      value: totalPrice,
      items: [
        {
          sku,
          item_id: id,
        },
      ],
    },
    context: {
      eventID: `purchase-${transactionId}`,
    },
  });
  tiktokLogEvent(
    'Purchase',
    {
      content_type: 'product',
      content_ids: [String(id)],
      content_name: sku,
      currency,
      quantity: 1,
      value: totalPrice,
    },
    transactionId.length > 0 ? {event_id: transactionId} : undefined
  );
}
